/**
 * Visitor Analytics Tracking
 * Tracks page views for admin dashboard analytics
 */

import { supabase, isSupabaseConfigured } from './supabase'

// Generate or get visitor ID (persisted in localStorage)
function getVisitorId() {
    if (typeof window === 'undefined') return null

    let visitorId = localStorage.getItem('inkspot_visitor_id')
    if (!visitorId) {
        visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        localStorage.setItem('inkspot_visitor_id', visitorId)
    }
    return visitorId
}

// Generate session ID (persisted in sessionStorage)
function getSessionId() {
    if (typeof window === 'undefined') return null

    let sessionId = sessionStorage.getItem('inkspot_session_id')
    if (!sessionId) {
        sessionId = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        sessionStorage.setItem('inkspot_session_id', sessionId)
    }
    return sessionId
}

// Detect device type
function getDeviceType() {
    if (typeof window === 'undefined') return 'desktop'

    const ua = navigator.userAgent
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet'
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile'
    return 'desktop'
}

// Detect browser
function getBrowser() {
    if (typeof window === 'undefined') return 'unknown'

    const ua = navigator.userAgent
    if (ua.includes('Firefox')) return 'Firefox'
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome'
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari'
    if (ua.includes('Edg')) return 'Edge'
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera'
    return 'Other'
}

// Detect OS
function getOS() {
    if (typeof window === 'undefined') return 'unknown'

    const ua = navigator.userAgent
    if (ua.includes('Windows')) return 'Windows'
    if (ua.includes('Mac OS')) return 'macOS'
    if (ua.includes('Linux')) return 'Linux'
    if (ua.includes('Android')) return 'Android'
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
    return 'Other'
}

// Parse UTM parameters from URL
function getUTMParams() {
    if (typeof window === 'undefined') return {}

    const params = new URLSearchParams(window.location.search)
    return {
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
    }
}

// Admin emails to exclude from tracking
const ADMIN_EMAILS = [
    'leon@inkspot.app',
    'admin@inkspot.app',
    'leonferdian@gmail.com',
    'leonardoferdiano@gmail.com',
    'octopus.smartclient@gmail.com'
]

/**
 * Track a page view
 * Call this on each page load/navigation
 * Excludes admin users from tracking
 */
export async function trackPageView() {
    if (!isSupabaseConfigured()) return

    try {
        const { data: { user } } = await supabase.auth.getUser()

        // Skip tracking for admin users
        if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
            console.log('Admin user - skipping analytics tracking')
            return
        }

        const utm = getUTMParams()

        const pageView = {
            visitor_id: getVisitorId(),
            session_id: getSessionId(),
            user_id: user?.id || null,
            page_path: window.location.pathname,
            page_title: document.title,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent,
            device_type: getDeviceType(),
            browser: getBrowser(),
            os: getOS(),
            utm_source: utm.utm_source,
            utm_medium: utm.utm_medium,
            utm_campaign: utm.utm_campaign,
            viewed_at: new Date().toISOString(),
        }

        await supabase.from('page_views').insert(pageView)
    } catch (error) {
        // Silently fail - don't break the app for analytics
        console.error('Page view tracking error:', error)
    }
}

/**
 * Get visitor analytics summary (for admin dashboard)
 */
export async function getVisitorAnalytics() {
    if (!isSupabaseConfigured()) return null

    try {
        const now = new Date()
        const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000)
        const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000)

        // Get page views for different periods
        const [todayResult, weekResult, monthResult, topPagesResult, sourcesResult, devicesResult] = await Promise.all([
            // Today's stats
            supabase
                .from('page_views')
                .select('visitor_id, session_id', { count: 'exact' })
                .gte('viewed_at', new Date().toISOString().split('T')[0]),

            // Last 7 days
            supabase
                .from('page_views')
                .select('visitor_id, session_id', { count: 'exact' })
                .gte('viewed_at', sevenDaysAgo.toISOString()),

            // Last 30 days
            supabase
                .from('page_views')
                .select('visitor_id, session_id', { count: 'exact' })
                .gte('viewed_at', thirtyDaysAgo.toISOString()),

            // Top pages (last 7 days)
            supabase
                .from('page_views')
                .select('page_path')
                .gte('viewed_at', sevenDaysAgo.toISOString()),

            // Traffic sources
            supabase
                .from('page_views')
                .select('referrer, utm_source')
                .gte('viewed_at', sevenDaysAgo.toISOString()),

            // Device breakdown
            supabase
                .from('page_views')
                .select('device_type')
                .gte('viewed_at', sevenDaysAgo.toISOString()),
        ])

        // Calculate unique visitors
        const todayVisitors = new Set(todayResult.data?.map(d => d.visitor_id) || [])
        const weekVisitors = new Set(weekResult.data?.map(d => d.visitor_id) || [])
        const monthVisitors = new Set(monthResult.data?.map(d => d.visitor_id) || [])

        // Calculate top pages
        const pageCounts = {}
        topPagesResult.data?.forEach(d => {
            pageCounts[d.page_path] = (pageCounts[d.page_path] || 0) + 1
        })
        const topPages = Object.entries(pageCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([path, views]) => ({ path, views }))

        // Calculate traffic sources
        const sourceCounts = {}
        sourcesResult.data?.forEach(d => {
            let source = d.utm_source || 'direct'
            if (!d.utm_source && d.referrer) {
                if (d.referrer.includes('google')) source = 'google'
                else if (d.referrer.includes('twitter') || d.referrer.includes('x.com')) source = 'twitter'
                else if (d.referrer.includes('facebook')) source = 'facebook'
                else if (d.referrer.includes('linkedin')) source = 'linkedin'
                else source = 'other'
            }
            sourceCounts[source] = (sourceCounts[source] || 0) + 1
        })
        const trafficSources = Object.entries(sourceCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([source, count]) => ({ source, count }))

        // Calculate device breakdown
        const deviceCounts = { desktop: 0, mobile: 0, tablet: 0 }
        devicesResult.data?.forEach(d => {
            const type = d.device_type || 'desktop'
            deviceCounts[type] = (deviceCounts[type] || 0) + 1
        })
        const totalDevices = Object.values(deviceCounts).reduce((a, b) => a + b, 0)
        const devices = Object.entries(deviceCounts).map(([type, count]) => ({
            type,
            count,
            percentage: totalDevices > 0 ? ((count / totalDevices) * 100).toFixed(1) : 0,
        }))

        return {
            today: {
                pageViews: todayResult.count || 0,
                uniqueVisitors: todayVisitors.size,
            },
            week: {
                pageViews: weekResult.count || 0,
                uniqueVisitors: weekVisitors.size,
            },
            month: {
                pageViews: monthResult.count || 0,
                uniqueVisitors: monthVisitors.size,
            },
            topPages,
            trafficSources,
            devices,
        }
    } catch (error) {
        console.error('Analytics fetch error:', error)
        return null
    }
}
